/**
 * Inspector dashboard / assignment: detect Contract Manufacturing (CM) QA vs auction or classic buyer–seller QA.
 * Matches `InspectionCompletion` / `QAInspectionsList` CM rules.
 *
 * @param {object|null|undefined} row - List row, QA request, or inspection payload with optional cm_rfq_id / cm_quotation_id / context_source / _isCmQa
 * @returns {boolean}
 */
export function isCmQaInspectionRow(row) {
  if (!row) return false;
  if (row.context_source === 'contract_manufacturer') return true;
  if (row._isCmQa === true) return true;
  const a = row.cm_rfq_id != null && Number(row.cm_rfq_id) > 0;
  const b = row.cm_quotation_id != null && Number(row.cm_quotation_id) > 0;
  return a && b;
}

/**
 * Single report URL for all flows; `state.qaReportUi` hints InspectionCompletion which UI to prefer when data is ambiguous.
 *
 * @param {number|string} inspectionId
 * @param {object} [rowOrQr] - Normalized list row or `qaRequest` shape
 * @returns {{ pathname: string, state: { qaReportUi: 'cm_external' | 'legacy' } }}
 */
export function buildInspectionReportLocation(inspectionId, rowOrQr) {
  const cm = isCmQaInspectionRow(rowOrQr);
  return {
    pathname: `/auction/inspection/${inspectionId}/complete`,
    state: { qaReportUi: cm ? 'cm_external' : 'legacy' },
  };
}

export function normalizeInspectionStatus(inspection) {
  return String(inspection?.status || '').trim().toLowerCase();
}

export function getP2pAssignment(inspection) {
  return inspection?.p2p_assignment ?? inspection?.p2p_payment ?? null;
}

export function inspectionHasReportWork(inspection) {
  if (inspection?.overall_rating) return true;
  const report = inspection?.inspection_report;
  if (!report) return false;
  if (typeof report === 'string') {
    const trimmed = report.trim();
    return trimmed.length > 2 && trimmed !== '{}' && trimmed !== 'null';
  }
  if (typeof report === 'object') {
    return Boolean(
      (Array.isArray(report.items) && report.items.length > 0) ||
        Object.keys(report).length > 0,
    );
  }
  return false;
}

export function isInspectionActiveWork(inspection) {
  const status = normalizeInspectionStatus(inspection);
  return (
    status === 'in_progress' ||
    status === 'completed' ||
    Boolean(inspection?.completed_at) ||
    inspectionHasReportWork(inspection)
  );
}

/** P2P: job accepted, payment approval done, buyer has not paid — not actionable on My Inspections yet. */
export function isP2pWaitingForBuyerPayment(inspection) {
  const p = getP2pAssignment(inspection);
  return (
    p?.is_p2p_linked === true &&
    normalizeInspectionStatus(inspection) === 'scheduled' &&
    Boolean(p.job_accepted_at) &&
    p.buyer_payment_status !== 'paid' &&
    !p.payment_approval_pending &&
    !isInspectionActiveWork(inspection)
  );
}
